import React from "react";
import { summarizeReferenceResults } from "../helpFunctions/referenceResults";

const formatComparisons = (count) =>
  `${count} ${count === 1 ? "comparison" : "comparisons"}`;

const formatPercentage = (count, total) => {
  if (!total) return "0%";
  const percentage = (count / total) * 100;
  const rounded = Number.isInteger(percentage)
    ? percentage
    : Number(percentage.toFixed(1));
  return `${rounded}%`;
};

const formatOutcomeLabel = (result) =>
  `${result.candidateName}: ${formatPercentage(
    result.candidateWins,
    result.totalCount
  )} candidate wins, ${formatPercentage(result.ties, result.totalCount)} ties, ${
    formatPercentage(result.referenceWins, result.totalCount)
  } reference wins${
    result.equallyBad
      ? `, ${formatPercentage(result.equallyBad, result.totalCount)} equally bad`
      : ""
  }`;

const ReferenceResults = ({
  votes,
  referenceVariantId,
  referenceVariantName = "Reference",
  candidateVariants = [],
}) => {
  const results = summarizeReferenceResults(
    votes,
    referenceVariantId,
    candidateVariants
  );
  const totals = results.reduce(
    (summary, result) => ({
      comparisons: summary.comparisons + result.totalCount,
      candidateWins: summary.candidateWins + result.candidateWins,
      ties: summary.ties + result.ties,
      referenceWins: summary.referenceWins + result.referenceWins,
      equallyBad: summary.equallyBad + result.equallyBad,
    }),
    { comparisons: 0, candidateWins: 0, ties: 0, referenceWins: 0, equallyBad: 0 }
  );

  return (
    <section className="reference-results" aria-labelledby="reference-results-heading">
      <div className="reference-results-header">
        <div>
          <span className="reference-results-kicker">Reference benchmark</span>
          <h3 id="reference-results-heading">
            Candidate outcomes against <strong>{referenceVariantName}</strong>
          </h3>
          <p>
            Bars show preference direction; the outcome pills show exact counts.
            Ties and equally-bad votes stay visible separately.
          </p>
        </div>
      </div>

      {results.length === 0 ? (
        <p className="reference-results-empty">
          No reference comparisons are available for the selected filters.
        </p>
      ) : (
        <>
          <div className="reference-results-summary" aria-label="Overall outcomes">
            <div className="reference-results-summary-total">
              <strong>{totals.comparisons}</strong>
              <span>Total comparisons</span>
            </div>
            <div className="reference-results-summary-stat candidate">
              <span className="summary-stat-label">Candidate wins</span>
              <strong>{formatPercentage(totals.candidateWins, totals.comparisons)}</strong>
            </div>
            <div className="reference-results-summary-stat tie">
              <span className="summary-stat-label">Ties</span>
              <strong>{formatPercentage(totals.ties, totals.comparisons)}</strong>
            </div>
            <div className="reference-results-summary-stat reference">
              <span className="summary-stat-label">Reference wins</span>
              <strong>{formatPercentage(totals.referenceWins, totals.comparisons)}</strong>
            </div>
            {totals.equallyBad > 0 && (
              <div className="reference-results-summary-stat equally-bad">
                <span className="summary-stat-label">Equally bad</span>
                <strong>{formatPercentage(totals.equallyBad, totals.comparisons)}</strong>
              </div>
            )}
          </div>
          <div className="reference-results-axis" aria-hidden="true">
            <span />
            <div className="reference-results-axis-track">
              <span>Candidate wins</span>
              <span>Tie</span>
              <span>{referenceVariantName} wins</span>
            </div>
            <span />
          </div>
          <div className="reference-results-list" role="list">
            {results.map((result) => (
              <div className="reference-result-row" key={result.candidateId} role="listitem">
                <div className="reference-result-label">
                  <strong title={result.candidateName}>
                    {result.candidateName}
                  </strong>
                  <span>{formatComparisons(result.totalCount)}</span>
                </div>
                <div
                  className="reference-result-track"
                  role="img"
                  aria-label={formatOutcomeLabel(result)}
                >
                  <div className="reference-result-half candidate-half">
                    {result.candidateWins > 0 && (
                      <div
                        className="reference-result-segment candidate"
                        style={{ width: `${result.candidateWinPercentage}%` }}
                      />
                    )}
                  </div>
                  <div
                    className={`reference-result-tie-marker${
                      result.ties > 0 ? " has-ties" : ""
                    }`}
                    aria-hidden="true"
                  />
                  <div className="reference-result-half reference-half">
                    {result.referenceWins > 0 && (
                      <div
                        className="reference-result-segment reference"
                        style={{ width: `${result.referenceWinPercentage}%` }}
                      />
                    )}
                  </div>
                </div>
                <div className="reference-result-counts">
                  <span className="candidate-count">
                    C {formatPercentage(result.candidateWins, result.totalCount)}
                  </span>
                  <span className="tie-count">
                    T {formatPercentage(result.ties, result.totalCount)}
                  </span>
                  <span className="reference-count">
                    R {formatPercentage(result.referenceWins, result.totalCount)}
                  </span>
                  {result.equallyBad > 0 && (
                    <span className="equally-bad-count">
                      EB {formatPercentage(result.equallyBad, result.totalCount)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="reference-results-legend">
            <span><i className="legend-swatch candidate" /> Candidate wins</span>
            <span><i className="legend-swatch tie" /> Ties</span>
            <span><i className="legend-swatch reference" /> Reference wins</span>
            <span className="legend-note">EB = equally bad</span>
          </div>
        </>
      )}
    </section>
  );
};

export default ReferenceResults;
